import { Form, useFormContext } from "@/components/form";
import { CollapsibleSection } from "@/components/form/collapsible-section";
import {
    Stack,
    TextField,
    InputAdornment,
    IconButton
} from "@mui/material";
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import { Dialog, ModalContextExtractor, ModalHeader, useOpenModal } from "@/components/modal";
import type { Cliente } from "@/shared/types/cliente";
import { getClienteDisplayValue } from "../../utils/de-data-utils";
import ClienteInfoCard from "../cliente-info-card";
import ClienteManualSearch from "@/features/facturacion-electronica/components/cliente-manual-search";

interface DEGeneralInfoFieldsProps {
    nextInputRef?: React.MutableRefObject<any>;
    onClienteChange?: (cliente: Cliente | null) => void;
    cliente?: Cliente | null;
    clienteError?: boolean;
}

export default function DEGeneralInfoFields({
    onClienteChange,
    cliente,
    clienteError = false,
}: DEGeneralInfoFieldsProps) {
    const openModal = useOpenModal();
    const form = useFormContext();

    // Seleccion de contribuyente desde el modal de busqueda manual
    const handleClienteSelect = (cliente: Cliente) => {
        // Si tiene ID, guardarlo en el form (es de BD local)
        if (cliente.id) {
            form.setFieldValue("clienteId", cliente.id);
        } else {
            // Si no tiene ID (DNIT), limpiar el campo
            form.setFieldValue("clienteId", "");
        }
        // Actualizar el estado del contribuyente para mostrar en UI
        if (onClienteChange) {
            onClienteChange(cliente);
        }
    };

    // Limpiar contribuyente seleccionado
    const handleClearCliente = () => {
        form.setFieldValue("clienteId", "");
        if (onClienteChange) {
            onClienteChange(null);
        }
    };

    return (
        <CollapsibleSection title="Información General">
            <Stack gap={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="flex-start">
                    <Stack flex={{ xs: '1 1 100%', sm: '2 1 400px' }} minWidth="300px">
                        <TextField
                            label="Cliente (Receptor) *"
                            size="small"
                            fullWidth
                            value={getClienteDisplayValue(cliente)}
                            placeholder="Buscar cliente..."
                            error={clienteError && !cliente}
                            helperText={clienteError && !cliente ? "Cliente es requerido" : ""}
                            slotProps={{
                                input: {
                                    readOnly: true,
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            {cliente && (
                                                <IconButton size="small" onClick={handleClearCliente}>
                                                    <ClearIcon fontSize="small" />
                                                </IconButton>
                                            )}
                                            <IconButton size="small" onClick={() => openModal("searchCliente")}>
                                                <SearchIcon />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }
                            }}
                            sx={{ cursor: 'pointer' }}
                            onClick={() => !cliente && openModal("searchCliente")}
                        />
                        <ClienteInfoCard cliente={cliente || null} title="Receptor" />
                    </Stack>
                </Stack>

                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1.5} alignItems="flex-start" flexWrap="wrap">
                    <Stack flex={{ xs: '1 1 100%', sm: '1 1 200px' }} minWidth="180px">
                        <Form.Select name="indicadorPresencia" label="Indicador de Presencia *" size="small">
                            <Form.Select.Option value="1">Operación presencial</Form.Select.Option>
                            <Form.Select.Option value="2">Operación electrónica</Form.Select.Option>
                        </Form.Select>
                    </Stack>

                    <Stack flex={{ xs: '1 1 100%', sm: '1 1 300px' }} minWidth="200px">
                        <Form.TextField name="observaciones" label="Observaciones" size="small" fullWidth />
                    </Stack>
                </Stack>
            </Stack>

            <Dialog code="searchCliente" size="lg">
                <ModalHeader>Buscar Cliente</ModalHeader>
                <ModalContextExtractor>
                    {({ handleClose }) => (
                        <ClienteManualSearch
                            selectedClienteId={cliente?.id}
                            onClienteSelect={(cliente: Cliente) => {
                                handleClienteSelect(cliente);
                                handleClose();
                            }}
                        />
                    )}
                </ModalContextExtractor>
            </Dialog>
        </CollapsibleSection>
    );
}

